"use client";
import type { ILinksCollection, IModuleSizeGuideModal } from "@contentful-api/types/contentful";
import type { Document } from "@contentful/rich-text-types";
import { ModalTypes } from "@lib/enums/ModalTypes";
import type { FC, ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { IAuthorizationModules, IContactFormModules, ILiveChatModal } from "./types";
import { modalMap } from "./types";

type GlobalModalContextType = {
    showModal: (modalType: string, modalProps?: any) => void;
    hideModal: () => void;
    modalType: ModalTypes;
    modalProps: IAuthorizationModules;
};

const initialState: GlobalModalContextType = {
    showModal: () => {},
    hideModal: () => {},
    modalType: null,
    modalProps: null,
};

const GlobalModalContext = createContext(initialState);
export const useGlobalModalContext = () => {
    const context = useContext(GlobalModalContext);

    if (!context) {
        throw new Error("useGlobalModalContext must be used within a GlobalModalProvider");
    }
    return context;
};

interface ModalProps extends IAuthorizationModules {
    linkCollection?: ILinksCollection;
    sizeGuideModal?: IModuleSizeGuideModal;
    isOpen?: boolean;
    onClose?: () => void;
    giftWrappingModal?: Document;
}

interface GlobalModalProviderProps {
    authorizationModules: IAuthorizationModules;
    linkCollection: ILinksCollection;
    sizeGuideModal: IModuleSizeGuideModal;
    shippingDetailsModal: Document;
    contactFormModules: IContactFormModules;
    liveChatModal?: ILiveChatModal;
    giftWrappingModal: Document;
    children: ReactNode;
    newsletterPolicyAgreement?: object;
}

export const GlobalModalProvider: FC<GlobalModalProviderProps> = ({
    authorizationModules,
    linkCollection,
    sizeGuideModal,
    shippingDetailsModal,
    contactFormModules,
    liveChatModal,
    giftWrappingModal,
    children,
}) => {
    const [modalType, setModalType] = useState<ModalTypes>(null);
    const [modalProps, setModalProps] = useState<IAuthorizationModules>(null);

    const showModal = useCallback((_modalType: ModalTypes, _modalProps: IAuthorizationModules) => {
        setModalType((prevModalType) => (prevModalType === _modalType ? null : _modalType));
        setModalProps(_modalProps);
    }, []);

    const hideModal = useCallback(() => {
        setModalType(null);
        setModalProps(null);
    }, []);

    const renderComponent = () => {
        const ModalComponent = modalMap[modalType];

        if (!ModalComponent) {
            return null;
        }

        let props: ModalProps = modalProps;

        if (modalType === ModalTypes.base) {
            props = modalProps;
        }

        if (modalType === ModalTypes.chat) {
            props = {
                ...modalProps,
                ...liveChatModal,
            };
        }

        if (modalType === ModalTypes.giftWrapping) {
            props = {
                ...modalProps,
                giftWrappingModal,
            };
        }

        if (
            modalType === ModalTypes.createAccount ||
            modalType === ModalTypes.login ||
            modalType === ModalTypes.forgotPassword ||
            modalType === ModalTypes.upgradeAccount
        )
            props = {
                ...modalProps,
                ...authorizationModules,
            };

        if (modalType === ModalTypes.contactForm || modalType === ModalTypes.contactFormSubmitted) {
            props = {
                ...modalProps,
                ...contactFormModules,
            };
        }

        if (modalType === ModalTypes.thankYouForSignUp) props = { linkCollection, ...modalProps };

        if (modalType === ModalTypes.sizeGuide || modalType === ModalTypes.globalSizeGuide)
            props = {
                isOpen: true,
                onClose: hideModal,
                sizeGuideModal,
                ...modalProps,
            };

        if (modalType === ModalTypes.shippingDetails) {
            props = {
                isOpen: true,
                onClose: hideModal,
                ...shippingDetailsModal,
                ...modalProps,
            };
        }

        if (modalType === ModalTypes.addToWishList) {
            props = {
                isOpen: true,
                onClose: hideModal,
                ...authorizationModules,
                ...modalProps,
            };
        }

        if (modalType === ModalTypes.liveChat || modalType === ModalTypes.chatIsNotAvailable) {
            props = {
                ...modalProps,
                ...liveChatModal,
            };
        }

        return <ModalComponent {...props} />;
    };

    const providerValue = useMemo(
        () => ({ modalType, modalProps, showModal, hideModal }),

        [hideModal, modalProps, modalType, showModal]
    );

    return (
        <GlobalModalContext.Provider value={providerValue}>
            {renderComponent()}
            {children}
        </GlobalModalContext.Provider>
    );
};
